import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Lightbulb, HandHeart, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const WhySection = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });

  const pillars = [
    {
      icon: Lightbulb,
      title: t('why.pillar.1.title'),
      description: t('why.pillar.1.description'),
      link: "/approach",
      linkText: t('why.pillar.1.link'),
      accent: "from-primary to-primary/60"
    },
    {
      icon: HandHeart,
      title: t('why.pillar.2.title'),
      description: t('why.pillar.2.description'),
      link: "/about",
      linkText: t('why.pillar.2.link'),
      accent: "from-accent to-accent/60"
    },
    {
      icon: ShieldCheck,
      title: t('why.pillar.3.title'),
      description: t('why.pillar.3.description'),
      link: "/services",
      linkText: t('why.pillar.3.link'),
      accent: "from-primary to-accent"
    }
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="diensten"
      className="py-24 bg-background"
      aria-labelledby="why-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Main content - asymmetric layout */}
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Text content */}
            <div
              className={`transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <h2
                id="why-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground leading-tight"
              >
                {t('why.title')}
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8">
                {t('why.subtitle')}
              </p>
              <p className="text-lg text-muted-foreground/80 leading-relaxed">
                {t('why.description')}
              </p>
            </div>

            {/* Right: Visual pillars */}
            <div
              className={`space-y-6 transition-all duration-700 ease-out delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              {pillars.map((pillar, index) => {
                const Icon = pillar.icon;
                return (
                  <div
                    key={index}
                    className="group p-6 rounded-2xl bg-card border border-border/50 shadow-sm hover:shadow-lg transition-all duration-300"
                    style={{ transitionDelay: `${300 + index * 100}ms` }}
                  >
                    <div className="flex items-center gap-5 mb-3">
                      <div
                        className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pillar.accent} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
                      >
                        <Icon className="w-7 h-7 text-white" aria-hidden="true" />
                      </div>
                      <span className="text-xl font-semibold text-card-foreground">
                        {pillar.title}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3 pl-[76px]">
                      {pillar.description}
                    </p>
                    <div className="pl-[76px]">
                      <Link 
                        to={pillar.link}
                        className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                      >
                        {pillar.linkText}
                        <ArrowRight className="ml-1 w-4 h-4 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySection;
